import { useState, useEffect } from "react";
import {
  X, Bot, Send, ShieldCheck, Settings2, Cpu, Sparkles, ChevronDown, Key, Clock, RotateCw,
} from "lucide-react";

import { api, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function AIInspector({ onClose, jobId }) {
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "Hi! I'm your EzJOB! AI assistant. You can switch between Ollama (Local), Google Gemini, and ChatGPT/CODEX right from this panel to test and compare responses.",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Model & Provider Selection State
  const [provider, setProvider] = useState(() => localStorage.getItem("ezjob_ai_provider") || "ollama");
  const [model, setModel] = useState(() => localStorage.getItem("ezjob_ai_model") || "llama3.2:latest");
  const [effort, setEffort] = useState(() => localStorage.getItem("ezjob_ai_effort") || "medium");
  const [apiKey, setApiKey] = useState(() => localStorage.getItem(`ezjob_ai_key_${provider}`) || "");
  const [statusData, setStatusData] = useState(null);

  const [refreshingModels, setRefreshingModels] = useState(false);

  // Load server status & live available models and sync active model with real installed models
  const refreshModels = async (showToast = false) => {
    setRefreshingModels(true);
    try {
      const r = await api.get("/ai/status");
      setStatusData(r.data);
      if (provider === "ollama" && r.data?.ollama_models?.length) {
        const installed = r.data.ollama_models;
        const currentSaved = localStorage.getItem("ezjob_ai_model");
        // Only override if the current saved model is completely missing from installed models
        if (currentSaved && !installed.includes(currentSaved)) {
          const bestDefault = installed.includes("qwen3.5:4b") ? "qwen3.5:4b" : installed[0];
          setModel(bestDefault);
          localStorage.setItem("ezjob_ai_model", bestDefault);
        }
      }
      if (showToast) toast.success("Refreshed live model list from Ollama");
    } catch (e) {
      if (showToast) toast.error("Could not reach Ollama to refresh models");
    } finally {
      setRefreshingModels(false);
    }
  };

  // Fetch on mount and periodically every 30 seconds
  useEffect(() => {
    refreshModels();
    const interval = setInterval(() => {
      if (provider === "ollama") refreshModels();
    }, 30000);
    return () => clearInterval(interval);
  }, [provider]);



  // Update localStorage when configuration changes
  const handleProviderChange = (newProvider) => {
    setProvider(newProvider);
    localStorage.setItem("ezjob_ai_provider", newProvider);
    
    // Pick default model for this provider based on live installed models
    let defModel = "llama3.2:latest";
    if (newProvider === "ollama") {
      defModel = statusData?.ollama_models?.length ? statusData.ollama_models[0] : "qwen3.5:4b";
    } else if (newProvider === "gemini") {
      defModel = "gemini-2.5-flash";
    } else if (newProvider === "chatgpt") {
      defModel = "gpt-4o";
    } else if (newProvider === "openrouter") {
      defModel = "anthropic/claude-3.5-sonnet";
    }
    
    setModel(defModel);
    localStorage.setItem("ezjob_ai_model", defModel);

    // Retrieve saved key for this provider if any
    const savedKey = localStorage.getItem(`ezjob_ai_key_${newProvider}`) || "";
    setApiKey(savedKey);
  };


  const handleModelChange = (newModel) => {
    setModel(newModel);
    localStorage.setItem("ezjob_ai_model", newModel);
  };

  const handleEffortChange = (newEffort) => {
    setEffort(newEffort);
    localStorage.setItem("ezjob_ai_effort", newEffort);
  };

  const handleKeyChange = (newKey) => {
    setApiKey(newKey);
    localStorage.setItem(`ezjob_ai_key_${provider}`, newKey);
  };

  const send = async () => {
    if (!input.trim()) return;
    const q = input.trim();
    setMessages((m) => [...m, { role: "user", content: q }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/ai/assistant", {
        message: q,
        job_id: jobId,
        provider,
        model,
        effort,
        api_key: apiKey,
      });

      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: data.content || (data.error ? `Notice: ${data.error}` : "Assistant finished without returning text. Try 'low' effort or a smaller testing model."),
          unconfigured: data.configured === false,
          model: data.model || model,
          provider: data.provider || provider,
          latency: data.latency_ms,
          effort: data.effort,
        },
      ]);
    } catch (e) {
      toast.error(apiErr(e));
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: `Error: ${apiErr(e)}`,
          unconfigured: true,
          model,
          provider,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const [pullingModel, setPullingModel] = useState(false);

  const pullTestModel = async (targetModel = "llama3.2:1b") => {
    setPullingModel(true);
    toast.info(`Pulling lightweight model ${targetModel} in Ollama... this may take 30-60s`);
    try {
      const res = await api.post("/ai/pull-model", { model: targetModel });
      if (res.data?.status === "ok") {
        toast.success(`Installed ${targetModel}! Switching model now.`);
        await refreshModels();
        setModel(targetModel);
        localStorage.setItem("ezjob_ai_model", targetModel);
      } else {
        toast.error(res.data?.message || "Failed to pull model");
      }
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setPullingModel(false);
    }
  };

  // Determine model list for currently selected provider
  const availableModels = (() => {
    if (provider === "ollama") {
      if (statusData?.ollama_models_detailed?.length) {
        return statusData.ollama_models_detailed.map((m) => ({
          value: m.name,
          label: m.label || m.name,
          size_gb: m.size_gb,
        }));
      }
      if (statusData?.ollama_models?.length) {
        return statusData.ollama_models.map((m) => ({ value: m, label: m }));
      }
      return [
        { value: "qwen3.5:4b", label: "qwen3.5:4b" },
        { value: "llama3.2:latest", label: "llama3.2:latest" },
      ];
    }
    if (provider === "gemini") {
      return [
        { value: "gemini-2.5-flash", label: "gemini-2.5-flash (Fast & Accurate)" },
        { value: "gemini-2.5-pro", label: "gemini-2.5-pro (High Reasoning)" },
        { value: "gemini-1.5-flash", label: "gemini-1.5-flash" },
      ];
    }
    if (provider === "chatgpt") {
      return [
        { value: "gpt-4o", label: "gpt-4o (Flagship Omni)" },
        { value: "gpt-4o-mini", label: "gpt-4o-mini (Light & Fast)" },
        { value: "o3-mini", label: "o3-mini (Reasoning)" },
      ];
    }
    return [
      { value: "anthropic/claude-3.5-sonnet", label: "claude-3.5-sonnet" },
      { value: "openai/gpt-4o", label: "gpt-4o" },
    ];
  })();

  const isReasoningModel = provider !== "ollama" || model.toLowerCase().includes("deepseek-r1");

  return (
    <aside
      className="fixed right-0 inset-y-0 z-40 w-full sm:w-[420px] border-l border-border bg-card flex flex-col shadow-2xl animate-in slide-in-from-right duration-200"
      data-testid="ai-inspector"
    >
      {/* Top Header */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-border bg-muted/20">
        <div className="flex items-center gap-2 font-bold text-foreground">
          <Bot className="h-5 w-5 text-primary" />
          <span>EzJOB! AI Engine</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setSettingsOpen((v) => !v)}
            title="Configure Provider, Model & Effort"
            className={`p-1.5 rounded-md transition-colors ${
              settingsOpen
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:bg-muted hover:text-foreground"
            }`}
          >
            <Settings2 className="h-4 w-4" />
          </button>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            data-testid="ai-inspector-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Provider & Model Selectors Strip */}
      <div className="p-3 bg-muted/30 border-b border-border space-y-2.5">
        <div className="grid grid-cols-2 gap-2 text-xs">
          {/* Provider Select */}
          <div className="space-y-1">
            <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1">
              <Cpu className="h-3 w-3 text-primary" /> Provider
            </label>
            <select
              value={provider}
              onChange={(e) => handleProviderChange(e.target.value)}
              className="w-full rounded border border-border bg-background px-2 py-1.5 text-xs font-semibold text-foreground outline-none focus:ring-1 focus:ring-ring"
            >
              <option value="ollama">1. Ollama (Local)</option>
              <option value="gemini">2. Antigravity / Gemini</option>
              <option value="chatgpt">3. ChatGPT / CODEX</option>
              <option value="openrouter">4. OpenRouter</option>
            </select>
          </div>

          {/* Model Select */}
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                <Sparkles className="h-3 w-3 text-primary" /> Model
              </label>
              {provider === "ollama" && (
                <button
                  onClick={() => refreshModels(true)}
                  title="Refresh live models installed in Ollama"
                  className="text-[10px] font-semibold text-primary hover:underline flex items-center gap-0.5"
                >
                  <RotateCw className={`h-2.5 w-2.5 ${refreshingModels ? "animate-spin" : ""}`} />
                  Sync
                </button>
              )}
            </div>
            <select
              value={model}
              onChange={(e) => handleModelChange(e.target.value)}
              className="w-full rounded border border-border bg-background px-2 py-1.5 text-xs font-semibold text-foreground outline-none focus:ring-1 focus:ring-ring"
            >
              {availableModels.map((m) => (
                <option key={m.value || m} value={m.value || m}>{m.label || m}</option>
              ))}
            </select>
          </div>
        </div>


        {/* Reasoning Effort (where applicable) */}
        <div className="flex items-center justify-between pt-1 border-t border-border/40 text-xs">
          <span className="text-[11px] text-muted-foreground font-medium flex items-center gap-1">
            Reasoning Effort:
          </span>
          <div className="flex items-center gap-1">
            {["low", "medium", "high"].map((lvl) => (
              <button
                key={lvl}
                onClick={() => handleEffortChange(lvl)}
                disabled={!isReasoningModel}
                className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase transition-colors ${
                  effort === lvl
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground hover:text-foreground"
                } ${!isReasoningModel ? "opacity-40 cursor-not-allowed" : ""}`}
              >
                {lvl}
              </button>
            ))}
          </div>
        </div>

        {/* Ollama Lightweight Model Helper for fast testing */}
        {provider === "ollama" && (
          <div className="flex items-center justify-between text-[11px] bg-primary/5 rounded p-1.5 border border-primary/20">
            <span className="text-muted-foreground">
              ⚡ Need fast test? <strong>llama3.2:1b</strong> (1.3GB) runs instantly.
            </span>
            <button
              onClick={() => pullTestModel("llama3.2:1b")}
              disabled={pullingModel}
              className="text-[10px] bg-primary text-primary-foreground px-2 py-0.5 rounded font-semibold hover:opacity-90 disabled:opacity-50"
            >
              {pullingModel ? "Pulling..." : "Get 1B Model"}
            </button>
          </div>
        )}

        {/* Collapsible API Key Drawer for Cloud Providers */}
        {settingsOpen && provider !== "ollama" && (
          <div className="pt-2 border-t border-border space-y-1.5 animate-in fade-in duration-150">
            <div className="flex items-center justify-between text-[11px]">
              <label className="font-semibold text-foreground flex items-center gap-1">
                <Key className="h-3 w-3 text-amber-500" /> {provider === "gemini" ? "Google Gemini API Key" : "OpenAI API Key"}
              </label>
              <span className="text-[10px] text-muted-foreground">Stored locally in browser</span>
            </div>
            <input
              type="password"
              placeholder={provider === "gemini" ? "AIzaSy..." : "sk-..."}
              value={apiKey}
              onChange={(e) => handleKeyChange(e.target.value)}
              className="w-full rounded border border-border bg-background px-2.5 py-1 text-xs outline-none focus:ring-1 focus:ring-ring"
            />
          </div>
        )}

        {settingsOpen && provider === "ollama" && (
          <div className="pt-1 text-[11px] text-muted-foreground border-t border-border">
            Connecting locally to <code>http://127.0.0.1:11434</code> (No API key required).
          </div>
        )}
      </div>

      {/* Messages Feed */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.map((m, i) => (
          <div
            key={i}
            className={`text-sm rounded-lg p-3 space-y-1.5 ${
              m.role === "user"
                ? "bg-primary text-primary-foreground ml-6"
                : "bg-muted/80 text-foreground mr-4 border border-border/60"
            }`}
          >
            {m.role === "assistant" && (m.provider || m.model) && (
              <div className="flex items-center justify-between text-[10px] text-muted-foreground font-mono pb-1 border-b border-border/40">
                <span className="font-bold uppercase text-primary">{m.provider}</span>
                <span>{m.model} {m.latency ? `(${m.latency}ms)` : ""}</span>
              </div>
            )}
            {m.unconfigured && (
              <div className="flex items-center gap-1 text-xs font-semibold text-amber-500 mb-1">
                <ShieldCheck className="h-3.5 w-3.5" /> Notice
              </div>
            )}
            <p className="whitespace-pre-wrap leading-relaxed text-xs sm:text-sm">{m.content}</p>
          </div>
        ))}
        {loading && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground p-2">
            <Sparkles className="h-4 w-4 animate-spin text-primary" />
            <span>Consulting {provider} ({model}) with {effort} effort…</span>
          </div>
        )}
      </div>

      {/* Chat Input */}
      <div className="p-3 border-t border-border bg-muted/10 flex gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()}
          placeholder={`Ask ${provider} with ${model}…`}
          data-testid="ai-inspector-input"
          className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-xs sm:text-sm outline-none focus:ring-1 focus:ring-ring"
        />
        <Button size="icon" onClick={send} disabled={loading || !input.trim()} data-testid="ai-inspector-send">
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </aside>
  );
}
